import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Calendar } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import { UpdateMaintenanceScheduleDto } from '../types';
import MaintenanceScheduleForm from '../components/forms/MaintenanceScheduleForm';

const EditMaintenanceSchedulePage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const scheduleId = parseInt(id!);
	const [isFormDirty, setIsFormDirty] = useState(false);
	const lastNavigateAttemptRef = useRef<string | null>(null);
	const lastPromptAtRef = useRef<number>(0);
	const suppressNavigationRef = useRef<boolean>(false);

	const {
		data: scheduleResponse,
		isLoading: scheduleLoading,
		error: scheduleError
	} = useQuery({
		queryKey: ['maintenance-schedule', scheduleId],
		queryFn: () => apiService.getMaintenanceScheduleById(scheduleId),
		enabled: !!scheduleId
	});

	const updateScheduleMutation = useMutation({
		mutationFn: async ({ id, data }: { id: number; data: UpdateMaintenanceScheduleDto }) => {
			const result = await apiService.updateMaintenanceSchedule(id, data);
			return result;
		},
		onSuccess: (response) => {
			if (response.success && response.data) {
				queryClient.invalidateQueries({ queryKey: ['maintenance-schedules'] });
				queryClient.invalidateQueries({ queryKey: ['maintenance-schedule', scheduleId] });
				toast.success(`Maintenance schedule "${response.data.name}" updated successfully!`);
				navigate(`/maintenance-schedules`);
			} else {
				toast.error(`Error: ${response.message || 'Unknown error occurred'}`);
			}
		},
		onError: (error: any) => {
			console.error('Error updating schedule:', error);
			const errorMessage = error?.response?.data?.message ||
				error?.response?.data?.errors?.join(', ') ||
				error.message ||
				'Unknown error';
			toast.error(`Error updating schedule: ${errorMessage}`);
		}
	});

	const handleSubmit = (data: UpdateMaintenanceScheduleDto) => {
		suppressNavigationRef.current = true;
		updateScheduleMutation.mutate({ id: scheduleId, data });
	};

	const handleBackClick = () => {
		if (!isFormDirty) {
			navigate('/maintenance-schedules');
			return;
		}

		const now = Date.now();
		const targetPath = '/maintenance-schedules';
		const samePath = lastNavigateAttemptRef.current === targetPath;
		const withinWindow = now - lastPromptAtRef.current < 5000;

		if (samePath && withinWindow) {
			// proceed with navigation
			lastNavigateAttemptRef.current = null;
			lastPromptAtRef.current = 0;
			suppressNavigationRef.current = true;
			navigate(targetPath);
			return;
		}

		lastNavigateAttemptRef.current = targetPath;
		lastPromptAtRef.current = now;
		toast(() => (
			<span className="text-sm">
				You have unsaved changes. Click the back button again to leave.
			</span>
		), { id: 'schedule-back-warning', duration: 4000, icon: '⚠️' });
	};

	if (scheduleLoading) {
		return (
			<div className="p-4">
				<div className="animate-pulse">
					<div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
					<div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
				</div>
			</div>
		);
	}

	if (scheduleError || !scheduleResponse?.data) {
		return (
			<div className="p-4">
				<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
					<p className="text-red-800 dark:text-red-200">Error loading maintenance schedule. Please try again.</p>
				</div>
			</div>
		);
	}

	const schedule = scheduleResponse.data;

	return (
		<div className="p-4 space-y-6">
			{/* Header */}
			<div className="mb-2">
				<div className="flex items-center gap-2 mb-2">
					<Calendar className="h-6 w-6 text-gray-900 dark:text-gray-100" />
					<h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit Maintenance Schedule</h1>
				</div>
				{/* Back Link */}
				<button
					onClick={handleBackClick}
					className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
				>
					<ArrowLeft className="w-4 h-4" />
					<span>Back to Schedules</span>
				</button>
			</div>

			<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
				<MaintenanceScheduleForm
					schedule={schedule}
					onSubmit={handleSubmit}
					isLoading={updateScheduleMutation.isPending}
					error={updateScheduleMutation.error ? 'Failed to update schedule' : undefined}
					onFormDirtyChange={setIsFormDirty}
				/>
			</div>
		</div>
	);
};

export default EditMaintenanceSchedulePage;






